import {
  DEFAULT_PERSONALITY_SPEC,
  findPersonalityPreset,
  normalizeSliderValues,
} from '../npc/config';
import type {
  PersonalityOutputLength,
  PersonalityPresetKey,
  PersonalitySliders,
  PersonalitySpec,
} from '../types/npc';

const toOutputLength = (value: unknown): PersonalityOutputLength => {
  if (value === 'short' || value === 'long') {
    return value;
  }
  return 'medium';
};

const toSliders = (value: unknown, presetKey: PersonalityPresetKey): PersonalitySliders => {
  const preset = findPersonalityPreset(presetKey);
  if (!value || typeof value !== 'object') {
    return { ...preset.sliders };
  }

  const raw = value as Record<string, unknown>;
  return normalizeSliderValues({
    formality: Number(raw.formality ?? preset.sliders.formality),
    warmth: Number(raw.warmth ?? preset.sliders.warmth),
    brevity: Number(raw.brevity ?? preset.sliders.brevity),
    creativity: Number(raw.creativity ?? preset.sliders.creativity),
    assertiveness: Number(raw.assertiveness ?? preset.sliders.assertiveness),
    skepticism: Number(raw.skepticism ?? preset.sliders.skepticism),
    humor: Number(raw.humor ?? preset.sliders.humor),
  });
};

export const clonePersonalitySpec = (
  value: PersonalitySpec | null | undefined,
): PersonalitySpec | null => {
  if (!value) {
    return null;
  }

  return {
    preset: value.preset,
    sliders: { ...value.sliders },
    output: { ...value.output },
    mbti: value.mbti ? { ...value.mbti } : null,
    enneagram: value.enneagram ? { ...value.enneagram } : null,
  };
};

export const createDefaultPersonalitySpec = (): PersonalitySpec =>
  clonePersonalitySpec(DEFAULT_PERSONALITY_SPEC) ?? {
    preset: DEFAULT_PERSONALITY_SPEC.preset,
    sliders: { ...DEFAULT_PERSONALITY_SPEC.sliders },
    output: { ...DEFAULT_PERSONALITY_SPEC.output },
    mbti: null,
    enneagram: null,
  };

export const toPersonalitySpec = (value: unknown): PersonalitySpec | null => {
  if (value === null || value === undefined) {
    return null;
  }

  if (!value || typeof value !== 'object') {
    return createDefaultPersonalitySpec();
  }

  const candidate = value as Record<string, unknown>;
  const presetCandidate = String(candidate.preset ?? DEFAULT_PERSONALITY_SPEC.preset)
    .trim()
    .toLowerCase() as PersonalityPresetKey;
  const preset = findPersonalityPreset(presetCandidate).key;
  const output =
    candidate.output && typeof candidate.output === 'object'
      ? (candidate.output as Record<string, unknown>)
      : null;

  const mbti =
    candidate.mbti && typeof candidate.mbti === 'object'
      ? {
          code: String((candidate.mbti as Record<string, unknown>).code ?? '').trim(),
          label: String((candidate.mbti as Record<string, unknown>).label ?? '').trim(),
        }
      : null;

  const enneagram =
    candidate.enneagram && typeof candidate.enneagram === 'object'
      ? {
          num: Number((candidate.enneagram as Record<string, unknown>).num ?? Number.NaN),
          label: String((candidate.enneagram as Record<string, unknown>).label ?? '').trim(),
        }
      : null;

  return {
    preset,
    sliders: toSliders(candidate.sliders, preset),
    output: {
      length: toOutputLength(output?.length),
    },
    mbti: mbti?.code ? mbti : null,
    enneagram:
      Number.isFinite(enneagram?.num) && enneagram && enneagram.num >= 1 && enneagram.num <= 9
        ? { ...enneagram, num: Math.floor(enneagram.num) }
        : null,
  };
};
